import type {
  ClientWorkspaceViewDto,
  WorkspaceUpdateDto,
  WorkspaceCreateDto,
  ApiResponse
} from '@/types/company'
import type { AnalyticsViewDto, AttributionView, RawAnalyticsViewDto } from '@/types/analytics'
import { normalizeAnalyticsView } from '@/utils/analyticsNormalize'
import type { DashboardRange } from '@/types/dashboard'
import { apiClient } from './client'
import type { DownloadResult } from './client'

/** Build the `?useMock=&attributionView=` query string shared by the range-windowed analytics calls. */
function analyticsQuery(mock?: boolean, attributionView?: AttributionView): string {
  const params = new URLSearchParams()
  if (mock) params.set('useMock', 'true')
  if (attributionView) params.set('attributionView', attributionView)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

// Workspace API functions (separated from company.ts)
export const workspaceApi = {
  // Create workspace for a client company
  create: (request: WorkspaceCreateDto): Promise<ApiResponse<ClientWorkspaceViewDto>> =>
    apiClient.post(`/api/Workspaces/CreateWorkspace`, request),

  // Update workspace
  update: (workspace: WorkspaceUpdateDto): Promise<ApiResponse<ClientWorkspaceViewDto>> =>
    apiClient.put(`/api/Workspaces/UpdateWorkspace`, workspace),

  // Delete workspace (requires both workspace id and client company id per API contract)
  delete: (clientCompanyId: string, workspaceId: string): Promise<ApiResponse<void>> =>
    apiClient.delete(`/api/Workspaces/${workspaceId}/${clientCompanyId}`),

  // Check whether a proposed Workspace slug is available within the given ClientCompany.
  checkSlug: (clientCompanyId: string, slug: string): Promise<ApiResponse<{ slug: string; available: boolean }>> =>
    apiClient.get(`/api/ClientCompanies/${clientCompanyId}/Workspaces/CheckSlug?slug=${encodeURIComponent(slug)}`),

  // ORB-328 / ORB-373: workspace-level visitor analytics, summed across all Webs in the
  // workspace and windowed by range (current vs previous window, with delta). Follows the
  // dashboard-tile route convention (range in the path); `mock` plumbs straight through.
  // `attributionView` flips credited numbers between conversion-session and first-seen.
  getAnalytics: async (workspaceId: string, range: DashboardRange, mock?: boolean, attributionView?: AttributionView): Promise<ApiResponse<AnalyticsViewDto>> => {
    const url = `/api/Dashboards/${encodeURIComponent(workspaceId)}/Analytics/${encodeURIComponent(range)}`
    const resp = await apiClient.get<RawAnalyticsViewDto>(`${url}${analyticsQuery(mock, attributionView)}`)
    return { ...resp, data: resp.data ? normalizeAnalyticsView(resp.data) : undefined }
  },

  // ORB-373: download a CSV export of this workspace's visitor/session data. Gated
  // server-side by the export capability (403 → handled globally).
  exportAnalytics: (workspaceId: string, range: DashboardRange, mock?: boolean, attributionView?: AttributionView): Promise<DownloadResult> => {
    const url = `/api/Dashboards/${encodeURIComponent(workspaceId)}/Analytics/${encodeURIComponent(range)}/Export`
    return apiClient.download(`${url}${analyticsQuery(mock, attributionView)}`)
  },
}
